import { Text } from "../Typography/Text.js";
import { MenuContext } from "./MenuContext.js";
import MenuItem_module_default from "./MenuItem.module.js";
import classNames from "classnames";
import { isValidElement, useCallback, useContext } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import ChevronRightIcon from "@vector-im/compound-design-tokens/assets/web/icons/chevron-right";
import { Slot } from "@radix-ui/react-slot";
//#region src/components/Menu/MenuItem.tsx
/**
* An item within a menu, acting either as a navigation button, or simply a
* container for other interactive elements.
* Must be used within a compound Menu or other `menu` or `menubar` aria role subtree.
*/
var MenuItem = ({ as, className, Icon, label, labelProps, onSelect, kind = "primary", children, onClick: onClickProp, disabled, hideChevron, ...props }) => {
	const Component = as ?? "button";
	const context = useContext(MenuContext);
	const onClick = useCallback((e) => {
		onClickProp?.(e);
		if (onSelect !== null && context?.MenuItemWrapper == null) {
			const selectEvent = new CustomEvent("menu.itemSelect", {
				bubbles: true,
				cancelable: true
			});
			onSelect(selectEvent);
			if (!selectEvent.defaultPrevented) context?.onOpenChange(false);
		}
	}, [context, onSelect]);
	const iconIsReactElement = isValidElement(Icon);
	const componentIcon = Icon;
	const SvgIcon = Icon;
	const content = /* @__PURE__ */ jsxs(Component, {
		role: "menuitem",
		...props,
		className: classNames(className, MenuItem_module_default.item, {
			[MenuItem_module_default.interactive]: onSelect !== null,
			[MenuItem_module_default["no-label"]]: label === null,
			[MenuItem_module_default["no-icon"]]: !Icon,
			[MenuItem_module_default["disabled"]]: disabled
		}),
		"data-kind": kind,
		onClick,
		disabled: Component === "button" ? disabled : void 0,
		"aria-disabled": Component === "button" ? void 0 : disabled,
		children: [
			Icon && (iconIsReactElement ? /* @__PURE__ */ jsx(Slot, {
				className: MenuItem_module_default.icon,
				children: componentIcon
			}) : /* @__PURE__ */ jsx(SvgIcon, {
				width: 24,
				height: 24,
				className: MenuItem_module_default.icon,
				"aria-hidden": true
			})),
			label !== null && /* @__PURE__ */ jsx(Text, {
				className: classNames(MenuItem_module_default.label, labelProps?.className),
				size: "md",
				weight: "medium",
				as: "span",
				...labelProps,
				children: label
			}),
			!hideChevron && (Component === "button" || Component === "a") && /* @__PURE__ */ jsx(ChevronRightIcon, {
				width: 8,
				height: 24,
				className: MenuItem_module_default["nav-hint"],
				"aria-hidden": true,
				viewBox: "8 0 8 24"
			}),
			children
		]
	});
	return context?.MenuItemWrapper == null || onSelect === null ? content : /* @__PURE__ */ jsx(context.MenuItemWrapper, {
		onSelect,
		children: content
	});
};
//#endregion
export { MenuItem };

//# sourceMappingURL=MenuItem.js.map